import { useEffect, useState, type ReactNode } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { LoadingState, ErrorState, DemoDataNotice } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { useAuth } from "@/lib/auth-context";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import type { AutomationSettingsDTO, QualificationRules, QualificationRequiredField } from "@platform/shared";
import { QUALIFICATION_REQUIRED_FIELDS } from "@platform/shared";

/**
 * Shapes for the free-form JSON columns (`ai_behavior`, `business_info`,
 * `operating_hours`, `escalation_rules`, `notification_settings`). The
 * schema deliberately leaves these untyped at the DB layer (see migration
 * 0002's comment on `qualification_data`) -- these interfaces are the
 * *frontend's* current opinion of their shape, not a DB contract. A future
 * automation engine reading these should validate defensively rather than
 * assume this exact shape.
 */
interface AiBehavior {
  tone?: string;
  instructions?: string;
}
interface BusinessInfo {
  name?: string;
  phone?: string;
  services?: string[];
  description?: string;
}
interface OperatingHours {
  mon_fri?: string;
  sat?: string;
  sun?: string;
}
interface EscalationRules {
  escalateAfterMinutes?: number;
  notifyPhone?: string;
}
interface NotificationSettings {
  emailOnNewLead?: boolean;
  emailOnMissedCall?: boolean;
  smsOnUrgent?: boolean;
}

const DEFAULT_REQUIRED_FIELDS: QualificationRequiredField[] = ["name", "service", "address", "timeline"];

function splitList(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-5">
      <p className="font-medium text-ink">{title}</p>
      <p className="mt-0.5 text-sm text-ink-soft">{description}</p>
      <div className="mt-4 space-y-3">{children}</div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputClass =
  "w-full rounded-md border border-line bg-paper-raised px-3 py-2 text-sm text-ink focus:border-brand-500";

export function Automation() {
  const { organizations } = useAuth();
  const canEdit = ["owner", "admin"].includes(organizations[0]?.role ?? "");
  const { data, isLoading, error, refetch } = useApi<{ settings: AutomationSettingsDTO }>("/api/automation/settings");

  const [missedCallSmsEnabled, setMissedCallSmsEnabled] = useState(false);
  const [missedCallSmsTemplate, setMissedCallSmsTemplate] = useState("");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiBehavior, setAiBehavior] = useState<AiBehavior>({});
  const [qualifyingQuestions, setQualifyingQuestions] = useState<string[]>([]);
  const [requiredFields, setRequiredFields] = useState<QualificationRequiredField[]>(DEFAULT_REQUIRED_FIELDS);
  const [acceptedServices, setAcceptedServices] = useState("");
  const [serviceAreaKeywords, setServiceAreaKeywords] = useState("");
  const [minValueDollars, setMinValueDollars] = useState("");
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({});
  const [operatingHours, setOperatingHours] = useState<OperatingHours>({});
  const [escalationRules, setEscalationRules] = useState<EscalationRules>({});
  const [notifications, setNotifications] = useState<NotificationSettings>({});

  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const s = data.settings;
    setMissedCallSmsEnabled(s.missedCallSmsEnabled);
    setMissedCallSmsTemplate(s.missedCallSmsTemplate ?? "");
    setAiEnabled(s.aiEnabled);
    setAiBehavior((s.aiBehavior as AiBehavior | null) ?? {});
    setQualifyingQuestions((s.qualifyingQuestions as string[] | null) ?? []);
    const rules = (s.qualificationRules as QualificationRules | null) ?? {};
    setRequiredFields(rules.requiredFields ?? DEFAULT_REQUIRED_FIELDS);
    setAcceptedServices((rules.acceptedServices ?? []).join(", "));
    setServiceAreaKeywords((rules.serviceAreaKeywords ?? []).join(", "));
    setMinValueDollars(rules.minEstimatedValueCents != null ? String(rules.minEstimatedValueCents / 100) : "");
    setBusinessInfo((s.businessInfo as BusinessInfo | null) ?? {});
    setOperatingHours((s.operatingHours as OperatingHours | null) ?? {});
    setEscalationRules((s.escalationRules as EscalationRules | null) ?? {});
    setNotifications((s.notificationSettings as NotificationSettings | null) ?? {});
  }, [data]);

  async function handleSave() {
    setSaveState("saving");
    setSaveError(null);
    const minDollars = minValueDollars.trim() === "" ? null : Number(minValueDollars);
    if (minDollars !== null && (!Number.isFinite(minDollars) || minDollars < 0)) {
      setSaveState("error");
      setSaveError("Minimum job value must be a positive number.");
      return;
    }
    const qualificationRules: QualificationRules = {
      requiredFields,
      acceptedServices: splitList(acceptedServices),
      serviceAreaKeywords: splitList(serviceAreaKeywords),
      ...(minDollars !== null ? { minEstimatedValueCents: Math.round(minDollars * 100) } : {}),
    };
    try {
      await apiFetch("/api/automation/settings", {
        method: "PATCH",
        body: JSON.stringify({
          missedCallSmsEnabled,
          missedCallSmsTemplate,
          aiEnabled,
          aiBehavior,
          qualifyingQuestions,
          qualificationRules,
          businessInfo,
          operatingHours,
          escalationRules,
          notificationSettings: notifications,
        }),
      });
      setSaveState("saved");
      refetch();
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (err) {
      setSaveState("error");
      setSaveError(err instanceof ApiRequestError ? err.message : "Couldn't save settings.");
    }
  }

  if (isLoading) return <LoadingState label="Loading automation settings…" />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-ink">Automation</h1>
          <p className="mt-1 text-sm text-ink-soft">Configure how Callback should respond to missed calls.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {saveState === "saved" && <span className="text-sm text-success-500">Saved</span>}
            <Button onClick={handleSave} disabled={saveState === "saving"}>
              {saveState === "saving" ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
      </div>
      {saveError && <ErrorState message={saveError} />}

      <DemoDataNotice>
        Missed-call texts, AI replies and lead qualification all run from these settings. Changes apply to the next
        call or message; leads already created are not re-evaluated.
      </DemoDataNotice>

      <fieldset disabled={!canEdit} className="space-y-6 disabled:opacity-70">
        <SectionCard title="Missed-call SMS" description="The text sent automatically after a missed call.">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={missedCallSmsEnabled}
              onChange={(e) => setMissedCallSmsEnabled(e.target.checked)}
            />
            Send an SMS after a missed call
          </label>
          <Field label="Message template">
            <textarea
              className={inputClass}
              rows={2}
              value={missedCallSmsTemplate}
              onChange={(e) => setMissedCallSmsTemplate(e.target.value)}
              placeholder="Sorry we missed your call! What can we help with?"
            />
          </Field>
        </SectionCard>

        <SectionCard title="AI behavior" description="How the AI should carry the conversation once it starts.">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />
            Let AI qualify leads over SMS
          </label>
          <Field label="Tone">
            <input
              className={inputClass}
              value={aiBehavior.tone ?? ""}
              onChange={(e) => setAiBehavior((prev) => ({ ...prev, tone: e.target.value }))}
              placeholder="Friendly and to the point"
            />
          </Field>
          <Field label="Instructions">
            <textarea
              className={inputClass}
              rows={3}
              value={aiBehavior.instructions ?? ""}
              onChange={(e) => setAiBehavior((prev) => ({ ...prev, instructions: e.target.value }))}
              placeholder="Always confirm the service needed and the best callback number."
            />
          </Field>
        </SectionCard>

        <SectionCard title="Qualification questions" description="Questions Callback asks every caller.">
          {qualifyingQuestions.map((q, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={inputClass}
                value={q}
                onChange={(e) =>
                  setQualifyingQuestions((prev) => prev.map((item, idx) => (idx === i ? e.target.value : item)))
                }
              />
              <button
                type="button"
                onClick={() => setQualifyingQuestions((prev) => prev.filter((_, idx) => idx !== i))}
                className="shrink-0 text-sm text-ink-soft hover:text-signal-600"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setQualifyingQuestions((prev) => [...prev, ""])}
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            + Add a question
          </button>
        </SectionCard>

        <SectionCard
          title="Lead qualification rules"
          description="The AI proposes; these rules decide. A lead is only marked Qualified when the AI agrees and every rule below is met — anything unclear is flagged for your review instead."
        >
          <div>
            <span className="text-sm font-medium text-ink">Required before a lead counts as qualified</span>
            <div className="mt-2 flex flex-wrap gap-4">
              {QUALIFICATION_REQUIRED_FIELDS.map((field) => (
                <label key={field} className="flex items-center gap-2 text-sm capitalize text-ink">
                  <input
                    type="checkbox"
                    checked={requiredFields.includes(field)}
                    onChange={(e) =>
                      setRequiredFields((prev) => (e.target.checked ? [...prev, field] : prev.filter((f) => f !== field)))
                    }
                  />
                  {field}
                </label>
              ))}
            </div>
          </div>
          <Field label="Services you accept (comma separated — leave empty to accept any)">
            <input
              className={inputClass}
              value={acceptedServices}
              onChange={(e) => setAcceptedServices(e.target.value)}
              placeholder="plumbing, drain, water heater"
            />
          </Field>
          <Field label="Service area (city / ZIP keywords, comma separated — leave empty for anywhere)">
            <input
              className={inputClass}
              value={serviceAreaKeywords}
              onChange={(e) => setServiceAreaKeywords(e.target.value)}
              placeholder="Springfield, 62701"
            />
          </Field>
          <Field label="Minimum estimated job value (optional)">
            <input
              type="number"
              min="0"
              className={inputClass}
              value={minValueDollars}
              onChange={(e) => setMinValueDollars(e.target.value)}
              placeholder="150"
            />
          </Field>
        </SectionCard>

        <SectionCard title="Business information" description="Context Callback uses when talking to customers.">
          <Field label="Business name">
            <input
              className={inputClass}
              value={businessInfo.name ?? ""}
              onChange={(e) => setBusinessInfo((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Field>
          <Field label="Services offered (comma separated)">
            <input
              className={inputClass}
              value={(businessInfo.services ?? []).join(", ")}
              onChange={(e) =>
                setBusinessInfo((prev) => ({
                  ...prev,
                  services: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                }))
              }
            />
          </Field>
          <Field label="Description">
            <textarea
              className={inputClass}
              rows={2}
              value={businessInfo.description ?? ""}
              onChange={(e) => setBusinessInfo((prev) => ({ ...prev, description: e.target.value }))}
            />
          </Field>
        </SectionCard>

        <SectionCard title="Operating hours" description="When Callback should treat calls as after-hours.">
          <Field label="Mon–Fri">
            <input
              className={inputClass}
              value={operatingHours.mon_fri ?? ""}
              onChange={(e) => setOperatingHours((prev) => ({ ...prev, mon_fri: e.target.value }))}
              placeholder="08:00-18:00"
            />
          </Field>
          <Field label="Saturday">
            <input
              className={inputClass}
              value={operatingHours.sat ?? ""}
              onChange={(e) => setOperatingHours((prev) => ({ ...prev, sat: e.target.value }))}
              placeholder="09:00-13:00"
            />
          </Field>
          <Field label="Sunday">
            <input
              className={inputClass}
              value={operatingHours.sun ?? ""}
              onChange={(e) => setOperatingHours((prev) => ({ ...prev, sun: e.target.value }))}
              placeholder="closed"
            />
          </Field>
        </SectionCard>

        <SectionCard title="Escalation rules" description="When a human should be looped in instead of the AI.">
          <Field label="Escalate if unanswered after (minutes)">
            <input
              type="number"
              className={inputClass}
              value={escalationRules.escalateAfterMinutes ?? ""}
              onChange={(e) =>
                setEscalationRules((prev) => ({
                  ...prev,
                  escalateAfterMinutes: e.target.value === "" ? undefined : Number(e.target.value),
                }))
              }
            />
          </Field>
          <Field label="Escalation phone number">
            <input
              className={inputClass}
              value={escalationRules.notifyPhone ?? ""}
              onChange={(e) => setEscalationRules((prev) => ({ ...prev, notifyPhone: e.target.value }))}
            />
          </Field>
        </SectionCard>

        <SectionCard title="Notifications" description="What this business gets alerted about, and how.">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={notifications.emailOnNewLead ?? false}
              onChange={(e) => setNotifications((prev) => ({ ...prev, emailOnNewLead: e.target.checked }))}
            />
            Email when a new lead comes in
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={notifications.emailOnMissedCall ?? false}
              onChange={(e) => setNotifications((prev) => ({ ...prev, emailOnMissedCall: e.target.checked }))}
            />
            Email on every missed call
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={notifications.smsOnUrgent ?? false}
              onChange={(e) => setNotifications((prev) => ({ ...prev, smsOnUrgent: e.target.checked }))}
            />
            Text me for urgent/needs-human leads
          </label>
        </SectionCard>
      </fieldset>

      {!canEdit && (
        <p className="text-sm text-ink-soft">Only organization owners and admins can change automation settings.</p>
      )}
    </div>
  );
}
